import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getIndustryConfig, getUserTypeLabels } from '../../utils/industryConfig';

const ROLE_BADGE = {
  super_admin: 'danger', admin: 'warning', manager: 'info',
  team_lead: 'primary', faculty: 'success', student: 'secondary',
};

// Roles that belong to the "learners/family" bucket. Everyone NOT in this
// list is treated as an employee — this naturally catches trainer, teacher,
// doctor, nurse, developer, cashier, etc. across industries without
// having to maintain a long whitelist.
const NON_EMPLOYEE_ROLES = ['student', 'parent', 'patient', 'customer', 'guest'];
const isStudentUser  = (u) => u?.roleKey === 'student' || u?.userType === 'student';
const isEmployeeUser = (u) => !NON_EMPLOYEE_ROLES.includes(u?.roleKey) && !NON_EMPLOYEE_ROLES.includes(u?.userType);
const isCollegeStudentUser = (u) => isStudentUser(u) && String(u?.collegeAccess?.registrationSource || '').toLowerCase() === 'college_portal';
const isIndividualStudentUser = (u) => isStudentUser(u) && !isCollegeStudentUser(u);
const USER_TABS = new Set(['all', 'employees', 'students', 'college-students', 'pending', 'deleted']);
const DELETION_REASON_LABELS = {
  no_longer_needed: 'No longer using the platform',
  duplicate_account: 'Created another account',
  privacy_concerns: 'Privacy concerns',
  too_many_notifications: 'Too many notifications',
  missing_features: 'Missing required features',
  difficult_to_use: 'Difficult to use',
  other: 'Other',
};

export default function Users() {
  const { tenant } = useAuth();
  // Resolve industry slug from tenant (populated at login). Fallback to localStorage for refresh cases.
  const industrySlug = (tenant?.industry?.slug || tenant?.industryId?.slug || localStorage.getItem('tenant_industry_slug') || '').toLowerCase();
  const industryCfg  = useMemo(() => getIndustryConfig(industrySlug), [industrySlug]);
  const userTypeLabels = useMemo(() => getUserTypeLabels(industrySlug), [industrySlug]);

  const [users, setUsers]       = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [roles, setRoles]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab') || 'all';
    return USER_TABS.has(requested) ? requested : 'all';
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', password:'', phone:'', roleId:'', roleKey:'', department:'', designation:'', parentUserId:'', additionalRoleIds:[] });
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState({ show: false, message: '', type: 'success' });
  const [showPass, setShowPass] = useState(false);
  const [approveRoleMap, setApproveRoleMap] = useState({}); // { [userId]: roleId } — per-row role selection before approval

  const NON_EMPLOYEE_ROLES = ['student', 'parent', 'patient', 'customer', 'guest'];

  const load = useCallback(async () => {
    if (activeTab === 'deleted') {
      setLoading(false);
      return;
    }
    setLoading(true);
    const studentTab = ['students', 'college-students'].includes(activeTab);
    const r = await api.get('/users', { params: {
      page,
      limit: 20,
      search,
      roleKey: activeTab === 'employees' ? 'admin,manager,team_lead,faculty,teacher,trainer' : studentTab ? 'student' : undefined,
      studentSource: activeTab === 'college-students' ? 'college' : activeTab === 'students' ? 'individual' : undefined,
    } }).catch(() => ({ data: { records: [], total: 0 } }));
    const responseRecords = r.data.records || r.data.users || [];
    // Keep the College Students view isolated even if an older API deployment
    // returns an unfiltered user page during a rolling frontend/backend update.
    const records = activeTab === 'college-students'
      ? responseRecords.filter(isCollegeStudentUser)
      : responseRecords;
    setUsers(records);
    setTotal(r.data.total ?? records.length);
    if (page === 1 && !search) {
      api.get('/users', { params: { limit: 1000 } }).then(ra => setAllUsers(ra.data.records || ra.data.users || [])).catch(() => {});
    }
    setLoading(false);
  }, [page, search, activeTab]);

  const loadPending = useCallback(async () => {
    try {
      const r = await api.get('/users-pending');
      setPendingUsers(r.data.data || r.data.records || []);
    } catch { setPendingUsers([]); }
  }, []);

  const loadDeleted = useCallback(async () => {
    if (activeTab !== 'deleted') return;
    setDeletedLoading(true);
    try {
      const response = await api.get('/account-deletion/admin', { params: { page, limit: 20, search } });
      const records = response.data.records || response.data.data?.records || [];
      setDeletedAccounts(records);
      setDeletedTotal(response.data.total ?? response.data.data?.total ?? records.length);
    } catch {
      setDeletedAccounts([]);
      setDeletedTotal(0);
    } finally {
      setDeletedLoading(false);
    }
  }, [activeTab, page, search]);

  const loadDeletedCount = useCallback(async () => {
    try {
      const response = await api.get('/account-deletion/admin', { params: { page: 1, limit: 1 } });
      setDeletedTotal(response.data.total ?? response.data.data?.total ?? 0);
    } catch {
      setDeletedTotal(0);
    }
  }, []);

  useEffect(() => { load(); loadPending(); loadDeleted(); }, [load, loadPending, loadDeleted]);
  useEffect(() => { loadDeletedCount(); }, [loadDeletedCount]);
  useEffect(() => { api.get('/roles').then(r => setRoles(r.data.data || r.data.records || [])).catch(() => {}); }, []);

  const approveUser = async (userId) => {
    const selectedRoleId = approveRoleMap[userId];
    const roleKey = selectedRoleId ? (roles.find(r => r._id === selectedRoleId)?.key || '') : '';
    try {
      await api.patch(`/users/${userId}/approve`, selectedRoleId ? { roleId: selectedRoleId, roleKey } : {});
      setToast({ show: true, message: 'User approved and activated.', type: 'success' });
      setApproveRoleMap(m => { const c = { ...m }; delete c[userId]; return c; });
      loadPending();
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to approve', type: 'danger' });
    }
  };

  const rejectUser = async (userId) => {
    if (!window.confirm('Reject and delete this pending registration? This cannot be undone.')) return;
    try {
      // Hard delete — the soft delete (DELETE /users/:id) only flips
      // isActive to false, which is a no-op for users that are already
      // inactive (which every pending registration is by definition).
      await api.delete(`/users/${userId}/permanent`);
      setToast({ show: true, message: 'Pending registration deleted.', type: 'warning' });
      loadPending();
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to delete', type: 'danger' });
    }
  };

  // Permanent (hard) delete — used from the All Users tab to fully remove
  // a previously deactivated user from the database. Distinct from the
  // soft delete (`deactivate`) which just toggles isActive.
  const purgeUser = async (id, label) => {
    if (!window.confirm(`Permanently delete ${label || 'this user'}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${id}/permanent`);
      setToast({ show: true, message: 'User permanently deleted.', type: 'warning' });
      load();
      loadPending();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to delete', type: 'danger' });
    }
  };

  const openNew = () => { setEditing(null); setForm({ firstName:'', lastName:'', email:'', password:'Welcome@123', phone:'', roleId:'', roleKey:'', department:'', designation:'', userType:'', parentUserId:'', additionalRoleIds:[] }); setShowPass(false); setShowForm(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      firstName: u.firstName, lastName: u.lastName, email: u.email,
      phone: u.phone||'', roleId: u.roleId?._id||u.roleId||'', roleKey: u.roleKey||'',
      department: u.department||'', designation: u.designation||'',
      userType: u.userType||'', password: '',
      // Pull the existing parent linkage from customFields so the form
      // pre-selects it on edit.
      parentUserId: u.customFields?.parentUserId || '',
      // Pre-select the additional roles for re-edit.
      additionalRoleIds: Array.isArray(u.additionalRoleIds)
        ? u.additionalRoleIds.map(x => x?._id || x).filter(Boolean)
        : [],
    });
    setShowPass(false); setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      // Drop blank optional fields so the Mongoose schema defaults take effect
      // (userType enum would reject an empty string).
      ['userType', 'department', 'designation'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      // The Parent linkage lives inside `customFields.parentUserId` — only
      // applies to student records and is the single source of truth used by
      // class reminders, absence notifications and the parent's
      // attendance/batches scopes. Empty string clears the link.
      const isStudentForm = payload.roleKey === 'student';
      if (isStudentForm) {
        payload.customFields = { ...(editing?.customFields || {}) };
        if (payload.parentUserId) payload.customFields.parentUserId = payload.parentUserId;
        else delete payload.customFields.parentUserId;
      }
      delete payload.parentUserId;

      // Drop the primary role from the additional list if it accidentally
      // landed there — the backend caches keys from ids, so duplicates
      // would just create noise in the array.
      if (Array.isArray(payload.additionalRoleIds)) {
        payload.additionalRoleIds = payload.additionalRoleIds.filter(id => id && id !== payload.roleId);
      } else {
        payload.additionalRoleIds = [];
      }

      if (editing) await api.put(`/users/${editing._id}`, payload);
      else await api.post('/users', payload);
      setToast({ show: true, message: `User ${editing ? 'updated' : 'created'}!`, type: 'success' });
      setShowForm(false); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    await api.delete(`/users/${id}`);
    setToast({ show: true, message: 'User deactivated', type: 'warning' });
    load();
  };

  const reactivate = async (id) => {
    try {
      await api.put(`/users/${id}`, { isActive: true });
      setToast({ show: true, message: 'User reactivated', type: 'success' });
      load();
    } catch (e) { setToast({ show: true, message: e.response?.data?.message || 'Failed', type: 'danger' }); }
  };

  const tabUsers = users;

  const empCount  = allUsers.filter(isEmployeeUser).length;
  const stuCount  = allUsers.filter(isStudentUser).length;
  const individualStudentCount = allUsers.filter(isIndividualStudentUser).length;
  const collegeStudentCount = allUsers.filter(isCollegeStudentUser).length;
  const activeCount = allUsers.filter(u => u.isActive !== false).length;

  const columns = [
    { key: 'firstName', label: 'Name', render: (v, row) => (
      <div style={{ textTransform: 'uppercase' }}>
        <span className="fw-medium">{v} {row.lastName}</span>
        {row.phone && <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>{row.phone}</div>}
      </div>
    )},
    { key: 'email', label: 'Email', render: v => <span style={{ fontSize: 12, textTransform: 'uppercase' }}>{v}</span> },
    { key: 'roleKey', label: 'Role', render: (v, row) => (
      <span className={`badge bg-${ROLE_BADGE[v] || 'secondary'}`} style={{ textTransform: 'uppercase' }}>{row.roleId?.name || v || '—'}</span>
    )},
    { key: 'isActive', label: 'Status', render: v => <span className={`badge ${v !== false ? 'bg-success' : 'bg-danger'}`} style={{ textTransform: 'uppercase' }}>{v !== false ? 'Active' : 'Inactive'}</span> },
    { key: '_id', label: 'Actions', render: (v, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-outline-primary btn-sm" onClick={e => { e.stopPropagation(); openEdit(row); }} title="Edit"><i className="bi bi-pencil"></i></button>
        {row.isActive !== false ? (
          <button className="btn btn-outline-warning btn-sm" onClick={e => { e.stopPropagation(); deactivate(v); }} title="Deactivate"><i className="bi bi-person-x"></i></button>
        ) : (
          <button className="btn btn-outline-success btn-sm" onClick={e => { e.stopPropagation(); reactivate(v); }} title="Reactivate"><i className="bi bi-person-check"></i></button>
        )}
        <button className="btn btn-outline-danger btn-sm" onClick={e => { e.stopPropagation(); purgeUser(v, `${row.firstName} ${row.lastName}`); }} title="Delete permanently"><i className="bi bi-trash"></i></button>
      </div>
    )},
  ];
  const collegeColumns = [
    columns[0],
    columns[1],
    { key: 'collegeAccess', label: 'College', render: (access = {}) => (
      <div>
        <div className="fw-medium">{access.partnerCollegeName || access.partnerCollegeId?.name || 'College not available'}</div>
        <small className="text-muted">{access.partnerCollegeCode || access.partnerCollegeId?.code || access.collegePortalCode || 'No code'}</small>
      </div>
    )},
    { key: 'collegeAccess', label: 'Academic Details', render: (access = {}) => (
      <div style={{ fontSize: 12 }}>
        <div>{access.declaredDepartment || 'Department not provided'}</div>
        <small className="text-muted">{access.declaredPassedOutYear ? `Year: ${access.declaredPassedOutYear}` : 'Year not provided'}</small>
      </div>
    )},
    { key: 'collegeAccess', label: 'Authorization', render: (access = {}) => {
      const status = String(access.status || 'not_applicable');
      const color = status === 'authorized' ? 'success' : status === 'pending_review' ? 'warning text-dark' : status === 'rejected' ? 'danger' : 'secondary';
      return <span className={`badge bg-${color}`}>{status.replace(/_/g, ' ')}</span>;
    }},
    { key: 'createdAt', label: 'Registered', render: value => <span style={{ fontSize: 12 }}>{value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span> },
    columns[3],
    columns[4],
  ];

  return (
    <div>
      <PageHeader title="Users" icon="bi-people"
        actions={
          <div className="d-flex gap-2">
            <div className="input-group" style={{ width: 240 }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input type="text" className="form-control" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={openNew}><i className="bi bi-plus-lg me-1"></i>Add User</button>
          </div>
        }
      />

      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between"><span>{editing ? 'Edit User' : 'Add New User'}</span><button className="btn-close" onClick={() => setShowForm(false)}></button></div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-3"><label className="form-label">First Name *</label><input className="form-control" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required /></div>
                <div className="col-md-3"><label className="form-label">Last Name *</label><input className="form-control" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required /></div>
                <div className="col-md-4"><label className="form-label">Email *</label><input type="email" className="form-control" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
                <div className="col-md-2"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="col-md-6">
                  <label className="form-label">Role <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.roleId}
                          onChange={e => {
                            const r = roles.find(r => r._id === e.target.value);
                            setForm(f => ({ ...f, roleId: e.target.value, roleKey: r?.key || '' }));
                          }} required>
                    <option value="">Select role...</option>
                    {roles.map(r => <option key={r._id} value={r._id}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>)}
                  </select>
                  <small className="text-muted">Role determines what this user can access.</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    {editing ? 'Reset Password' : 'Password'}
                    {editing && <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>(leave blank to keep current)</span>}
                  </label>
                  <div className="input-group">
                    <input
                      type={showPass ? 'text' : 'password'}
                      className="form-control"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder={editing ? 'Enter new password...' : 'Welcome@123'}
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPass(p => !p)} tabIndex={-1}>
                      <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>

                {/* Additional roles — only meaningful for non-student users.
                    A user (e.g. Admin who also teaches) can carry extra roles
                    on top of the primary one. The sidebar merges every
                    role's modules and permission matrices, so an admin+faculty
                    user keeps full admin powers AND gets faculty-only views
                    like "My Batches" / Course Records / batch-faculty pickers. */}
                {form.roleKey && form.roleKey !== 'student' && (
                  <div className="col-12">
                    <label className="form-label">
                      <i className="bi bi-stack me-1 text-info"/>
                      Additional Roles <span className="text-muted fw-normal" style={{ fontSize: 11 }}>(optional)</span>
                    </label>
                    <div className="d-flex flex-wrap gap-2 p-2 border rounded" style={{ background: '#f8fafc', minHeight: 44 }}>
                      {roles
                        .filter(r => r._id !== form.roleId && r.isActive !== false)
                        .map(r => {
                          const checked = form.additionalRoleIds?.includes(r._id);
                          return (
                            <label
                              key={r._id}
                              className={`px-2 py-1 rounded border d-flex align-items-center gap-1 ${checked ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'}`}
                              style={{ cursor: 'pointer', fontSize: 12, userSelect: 'none' }}
                            >
                              <input
                                type="checkbox"
                                checked={checked || false}
                                onChange={e => setForm(f => ({
                                  ...f,
                                  additionalRoleIds: e.target.checked
                                    ? [...(f.additionalRoleIds || []), r._id]
                                    : (f.additionalRoleIds || []).filter(id => id !== r._id),
                                }))}
                                style={{ marginRight: 4 }}
                              />
                              {r.name}
                            </label>
                          );
                        })}
                      {roles.length <= 1 && (
                        <span className="text-muted small">No other roles available — create more in Roles & Permissions.</span>
                      )}
                    </div>
                    <small className="text-muted">
                      Modules and permissions from these roles are merged with the primary one.
                      E.g. tick <strong>Faculty</strong> on an Admin to give them My Batches, Course Records and per-batch attendance.
                    </small>
                  </div>
                )}

                {/* Parent linkage — shown only when this user is a Student.
                    Picks any user in the tenant as the parent. The chosen
                    user's _id is stored in `customFields.parentUserId` and
                    drives the parent's attendance scope, batch dropdown,
                    class reminders and absence notifications. */}
                {form.roleKey === 'student' && (
                  <div className="col-12">
                    <label className="form-label">
                      <i className="bi bi-person-heart me-1 text-pink"/>
                      Parent / Guardian <span className="text-muted fw-normal" style={{ fontSize: 11 }}>(optional)</span>
                    </label>
                    <select
                      className="form-select"
                      value={form.parentUserId || ''}
                      onChange={e => setForm(f => ({ ...f, parentUserId: e.target.value }))}
                    >
                      <option value="">— No parent linked —</option>
                      {allUsers
                        .filter(u => u._id !== editing?._id && u.roleKey !== 'student' && u.isActive !== false)
                        .map(u => (
                          <option key={u._id} value={u._id}>
                            {u.firstName} {u.lastName} ({u.email}){u.roleKey ? ` · ${u.roleKey.replace(/_/g,' ')}` : ''}
                          </option>
                        ))}
                    </select>
                    <small className="text-muted">
                      The linked user receives this student's class reminders and absence
                      notifications, and sees only this child's attendance + batches.
                    </small>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving && <span className="spinner-border spinner-border-sm me-1"></span>}{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats bar */}
      {allUsers.length > 0 && (
        <div className="row g-2 mb-3">
          {[
            { label: 'Total Users', value: allUsers.length, icon: 'bi-people', color: 'primary' },
            { label: 'Active',      value: activeCount,     icon: 'bi-check-circle', color: 'success' },
            { label: 'Employees',   value: empCount,        icon: 'bi-person-badge', color: 'info' },
            { label: 'Students',    value: stuCount,        icon: 'bi-mortarboard', color: 'warning' },
          ].map(s => (
            <div className="col-6 col-md-3" key={s.label}>
              <div className="card border-0 shadow-sm">
                <div className="card-body py-2 d-flex align-items-center gap-2">
                  <i className={`bi ${s.icon} fs-5 text-${s.color}`}></i>
                  <div><div className="fw-bold lh-1">{s.value}</div><small className="text-muted">{s.label}</small></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'all',       label: 'All Users', count: allUsers.length, badge: 'secondary' },
          { key: 'employees', label: 'Employees', count: empCount,        badge: 'secondary' },
          { key: 'students',  label: 'Individual Students', count: individualStudentCount, badge: 'secondary' },
          { key: 'college-students', label: 'College Students', count: collegeStudentCount, badge: collegeStudentCount > 0 ? 'primary' : 'secondary' },
          { key: 'pending',   label: 'Pending Approvals', count: pendingUsers.length, badge: pendingUsers.length > 0 ? 'warning' : 'secondary' },
          { key: 'deleted',   label: 'Deleted Accounts', count: deletedTotal, badge: deletedTotal > 0 ? 'danger' : 'secondary' },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${activeTab === t.key ? 'active' : ''}`} onClick={() => { setActiveTab(t.key); setPage(1); }}>
              {t.key === 'pending' && <i className="bi bi-hourglass-split me-1"></i>}
              {t.label} <span className={`badge bg-${t.badge} ms-1`} style={{ fontSize: '0.65rem' }}>{t.count}</span>
            </button>
          </li>
        ))}
      </ul>

      {activeTab === 'deleted' ? (
        <div className="card">
          <div className="card-body p-0">
            {deletedLoading ? (
              <div className="py-5 text-center"><LoadingSpinner /></div>
            ) : deletedAccounts.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-person-x fs-1 d-block mb-2" style={{ color: '#94a3b8' }}></i>
                <div className="fw-semibold">No deleted accounts</div>
                <div style={{ fontSize: 12 }}>Student account deletion records will appear here.</div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead style={{ fontSize: 12, background: '#f8fafc' }}>
                    <tr>
                      <th>Student</th>
                      <th>Contact</th>
                      <th>Source</th>
                      <th>Reason</th>
                      <th>Deleted</th>
                      <th>Notification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedAccounts.map((record) => {
                      const snapshot = record.userSnapshot || {};
                      const notificationStatus = record.notification?.status || 'pending';
                      const notificationColor = notificationStatus === 'sent'
                        ? 'success'
                        : notificationStatus === 'partial' ? 'warning text-dark' : notificationStatus === 'no_recipients' ? 'secondary' : 'danger';
                      return (
                        <tr key={record._id}>
                          <td>
                            <div className="fw-medium">{snapshot.firstName} {snapshot.lastName}</div>
                            <small className="text-muted">{snapshot.roleKey || 'student'}</small>
                          </td>
                          <td>
                            <div style={{ fontSize: 12 }}>{snapshot.email || '-'}</div>
                            <small className="text-muted">{snapshot.phone || 'No phone'}</small>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">{String(snapshot.registrationSource || 'direct').replace(/_/g, ' ')}</span>
                            {snapshot.collegeName && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{snapshot.collegeName}</div>}
                          </td>
                          <td style={{ minWidth: 190 }}>
                            <div style={{ fontSize: 12 }}>{DELETION_REASON_LABELS[record.reasonCode] || record.reasonCode || '-'}</div>
                            {record.reasonText && <small className="text-muted d-block mt-1">{record.reasonText}</small>}
                          </td>
                          <td style={{ fontSize: 12 }}>{record.deactivatedAt ? new Date(record.deactivatedAt).toLocaleString('en-IN') : '-'}</td>
                          <td><span className={`badge bg-${notificationColor}`}>{notificationStatus.replace(/_/g, ' ')}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {deletedTotal > 20 && (
            <div className="card-footer bg-white">
              <Pagination page={page} totalPages={Math.ceil(deletedTotal / 20)} onPageChange={setPage} total={deletedTotal} limit={20} />
            </div>
          )}
        </div>
      ) : activeTab === 'pending' ? (
        <div className="card">
          <div className="card-body">
            {pendingUsers.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-check-circle fs-1 d-block mb-2" style={{ color: '#22c55e' }}></i>
                <div className="fw-semibold">No pending approvals</div>
                <div style={{ fontSize: 12 }}>When new users self-register via the tenant registration link, they'll appear here for your approval.</div>
              </div>
            ) : (
              <>
                <div className="alert alert-warning d-flex gap-2 align-items-start py-2 mb-3" style={{ fontSize: 13 }}>
                  <i className="bi bi-info-circle-fill mt-1"></i>
                  <div>
                    <strong>{pendingUsers.length}</strong> user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval.
                    Assign a role and click <strong>Approve</strong> to activate their account. Users cannot log in until approved.
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead style={{ fontSize: 12, background: '#f8fafc' }}>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Registered</th>
                        <th style={{ width: 200 }}>Assign Role</th>
                        <th style={{ width: 180 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingUsers.map(u => (
                        <tr key={u._id}>
                          <td style={{ textTransform: 'uppercase' }}><span className="fw-medium">{u.firstName} {u.lastName}</span></td>
                          <td style={{ fontSize: 12, textTransform: 'uppercase' }}>{u.email}</td>
                          <td style={{ fontSize: 12, textTransform: 'uppercase' }}>{u.phone || '—'}</td>
                          <td style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>{new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={approveRoleMap[u._id] || ''}
                              onChange={e => setApproveRoleMap(m => ({ ...m, [u._id]: e.target.value }))}
                            >
                              <option value="">— Keep default role —</option>
                              {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                            </select>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-success btn-sm"
                                style={{ fontSize: 12 }}
                                onClick={() => approveUser(u._id)}
                                title="Approve and activate this user"
                              >
                                <i className="bi bi-check-lg me-1"></i>Approve
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                style={{ fontSize: 12 }}
                                onClick={() => rejectUser(u._id)}
                                title="Reject and delete this pending registration"
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-body p-0">
              <DataTable columns={activeTab === 'college-students' ? collegeColumns : columns} data={tabUsers} loading={loading} emptyTitle={activeTab === 'college-students' ? 'No college students found' : 'No users found'} emptyIcon={activeTab === 'college-students' ? 'bi-buildings' : 'bi-people'} />
            </div>
          </div>
          <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />
        </>
      )}
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
